import React, { Component } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Pagination, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CustomizedSnackbars from '../../components/material-snackbars';
import Api from '../../services/api';
import UsageService from '../../services/usage.service';
import UsagePeriodFilter, { presetRange } from '../../components/usage/usage-period-filter';
import UsageVisitAccordion from '../../components/usage/usage-visit-accordion';
import UsageEventDrawer from '../../components/usage/usage-event-drawer';
import { UsageKpiCard, UsageKindChip, UsageStatusChip, UsageApiChip, UsageDurationBar, UsageEmpty, UsageEllipsisCell, UsageDirectApiChip, fmtDateTime, fmtMs, fmtInt, kindLabel } from '../../components/usage/usage-widgets';
import { t } from '../../components/usage/usage-i18n';

// ---------------------------------------------------------------------------
// Linha do tempo de UM usuário (investigação). Lê o fato por índice (S1/S11).
// Duas leituras: por VISITA (accordion tela → filhos) e plana (tabela com filtros finos).
// Chega com ?userId=&userName=&start=&end= pelos links do dashboard e das recusas.
// ---------------------------------------------------------------------------

const KINDS = ['', 'read', 'write', 'export', 'login', 'logout', 'screen', 'denied', 'role_denied', 'blocked', 'rate_limited', 'would_deny'];
const PAGE_SIZE = 20;

function parseQuery(search) {
  const q = new URLSearchParams(search || '');
  const start = q.get('start'); const end = q.get('end');
  const range = start && end
    ? { preset: 'custom', dateStart: moment(start).startOf('day').format('YYYY/MM/DD HH:mm:ss'), dateEnd: moment(end).endOf('day').format('YYYY/MM/DD HH:mm:ss') }
    : { preset: '7d', ...presetRange('7d') };
  return { ...range, userId: Number(q.get('userId')) || 0, userName: q.get('userName') || '', api: '', actionGroup: '' };
}

export default class UsageUserActivity extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filter: parseQuery(props.location?.search),
      actionKind: '', entityType: '', entityId: '',
      mode: 'visits', autoFlat: false, loading: false, loaded: false,
      summary: null, visits: [], rows: [], count: 0, page: 1,
      drawerRow: null, snack: { open: false, variant: 'error', message: '' },
    };
    this.onFilter = this.onFilter.bind(this);
    this.load = this.load.bind(this);
    this.loadVisitRows = this.loadVisitRows.bind(this);
    this.openRow = this.openRow.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.exportCsv = this.exportCsv.bind(this);
    this.onPage = this.onPage.bind(this);
    this.closeSnack = this.closeSnack.bind(this);
  }

  componentDidMount() { this.lastSearch = this.props.location?.search || ''; if (this.state.filter.userId) this.load(1); }

  // Montada permanentemente (troca de aba não perde a consulta). Querystring nova com a aba ativa
  // (ranking → linha do tempo) → novos filtros + busca.
  componentDidUpdate() {
    const search = this.props.location?.search || '';
    if (this.props.active && search && search !== this.lastSearch) {
      this.lastSearch = search;
      this.setState({ filter: parseQuery(search), loaded: false, autoFlat: false, visits: [], rows: [], count: 0 }, () => { if (this.state.filter.userId) this.load(1); });
    }
  }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }
  onFilter(next) { this.setState({ filter: next }); }
  closeSnack() { this.setState({ snack: { ...this.state.snack, open: false } }); }
  openRow(row) { this.setState({ drawerRow: row }); }
  closeDrawer() { this.setState({ drawerRow: null }); }
  onPage(e, page) { this.load(page); }
  notify(variant, message) { this.setState({ snack: { open: true, variant, message } }); }

  load(page) {
    const f = this.state.filter;
    if (!f.userId) { this.notify('error', this.tr('usage.ua.needUser', 'Informe o usuário.')); return; }
    this.setState({ loading: true, page });
    const summary = UsageService.summary({ ...f, screenRoute: '' });
    const list = this.state.mode === 'visits'
      ? UsageService.visits(f.userId, f.dateStart, f.dateEnd, page, PAGE_SIZE).then((r) => ({ visits: r.data, rows: [], count: r.count }))
      : this.flatQuery(page);
    Promise.all([summary, list]).then(([s, l]) => {
      // 'Por visita' só mostra o que passou pelas telas. Sem visita nenhuma (chamadas diretas à API:
      // URL colada, ferramenta, script), cai na lista plana sozinho e avisa — em vez de 'nada'.
      if (this.state.mode === 'visits' && page === 1 && !l.visits.length) {
        return this.flatQuery(1).then((flat) => {
          if (flat.rows.length) this.setState({ loading: false, loaded: true, summary: s.current, mode: 'flat', autoFlat: true, ...flat });
          else this.setState({ loading: false, loaded: true, summary: s.current, ...l });
        });
      }
      this.setState({ loading: false, loaded: true, summary: s.current, ...l });
      return null;
    }).catch((err) => { Api.kickoff(err); this.setState({ loading: false }); this.notify('error', err.message); });
  }

  flatQuery(page) {
    const f = this.state.filter;
    return UsageService.activity({ userId: f.userId, dateStart: f.dateStart, dateEnd: f.dateEnd, page, pageSize: PAGE_SIZE, actionKind: this.state.actionKind, entityType: this.state.entityType, entityId: this.state.entityId })
      .then((r) => ({ visits: [], rows: r.data, count: r.count }));
  }

  loadVisitRows(id) { return UsageService.visitRows(id); }

  exportCsv() {
    const f = this.state.filter;
    if (!f.userId) { this.notify('error', this.tr('usage.ua.needUser', 'Informe o usuário.')); return; }
    this.setState({ loading: true });
    UsageService.exportActivity({
      dateStart: moment(f.dateStart).format('YYYY-MM-DD'), dateEnd: moment(f.dateEnd).format('YYYY-MM-DD'),
      userId: f.userId, actionKind: this.state.actionKind || null, entityType: this.state.entityType || null, entityId: this.state.entityId ? Number(this.state.entityId) : null,
    }).then((r) => {
      this.setState({ loading: false });
      if (r && r.success) this.notify('success', this.tr('usage.ua.exportOk', 'Exportação solicitada. O arquivo chega por e-mail em alguns minutos.'));
      else this.notify('error', (r && r.errors && r.errors[0]) || this.tr('usage.ua.exportFail', 'Falha ao solicitar a exportação.'));
    }).catch((err) => { Api.kickoff(err); this.setState({ loading: false }); this.notify('error', err.message); });
  }

  renderSummary() {
    const { intl } = this.props;
    const s = this.state.summary; if (!s) return null;
    const cards = [
      { title: this.tr('usage.ua.kpi.requests', 'Requests'), value: s.reads + s.writes + s.exports, color: '#3F51B4' },
      { title: this.tr('usage.col.screens', 'Telas'), value: s.screens, color: '#9C27B0' },
      { title: this.tr('usage.col.writes', 'Escritas'), value: s.writes, color: '#8BC34A' },
      { title: this.tr('usage.col.exports', 'Exportações'), value: s.exports, color: '#FF9800' },
      { title: this.tr('usage.col.denials', 'Recusas'), value: s.denials, color: '#F44336' },
      { title: this.tr('usage.dash.kpi.errors', 'Erros 5xx'), value: s.errors, color: '#B71C1C' },
      { title: this.tr('usage.ua.kpi.avg', 'Duração média'), value: s.avgDurationMs, format: fmtMs, color: '#607D8B' },
      { title: this.tr('usage.ua.kpi.max', 'Máximo'), value: s.maxDurationMs, format: fmtMs, color: '#607D8B' },
    ];
    return <Grid container spacing={2} style={{ marginBottom: 12 }}>{cards.map((c) => <Grid item xs={6} sm={3} lg={1.5} key={c.title}><UsageKpiCard intl={intl} {...c} /></Grid>)}</Grid>;
  }

  renderFlat() {
    const { intl } = this.props;
    const { rows } = this.state;
    if (!rows.length) return <UsageEmpty intl={intl} compact title={this.tr('usage.ua.noEvents', 'Nenhum evento com esses filtros')} />;
    const maxMs = Math.max(1000, ...rows.map((r) => Number(r.durationMs) || 0));
    return (
      <Box style={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>{this.tr('usage.col.when', 'Quando')}</TableCell><TableCell>{this.tr('usage.col.action', 'Ação')}</TableCell><TableCell>API</TableCell><TableCell>{this.tr('usage.col.routeOrScreen', 'Rota / tela')}</TableCell>
            <TableCell>{this.tr('usage.col.screen', 'Tela')}</TableCell><TableCell>{this.tr('usage.col.entity', 'Entidade')}</TableCell><TableCell>Status</TableCell><TableCell>{this.tr('usage.col.duration', 'Duração')}</TableCell><TableCell>IP</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover style={{ cursor: 'pointer', background: r.actionKind === 'screen' ? '#faf5ff' : undefined }} onClick={() => this.openRow(r)}>
                <TableCell style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(r.occurredAt, true)}</TableCell>
                <TableCell><UsageKindChip intl={intl} kind={r.actionKind} details={r.details} /></TableCell>
                <TableCell><UsageApiChip api={r.api} /></TableCell>
                <TableCell><UsageEllipsisCell text={r.actionKind === 'screen' ? r.path : r.route} title={r.url} maxWidth={280} /></TableCell>
                <TableCell style={{ color: '#888' }}>{!r.screenVisitId && r.actionKind !== 'screen' ? <UsageDirectApiChip intl={intl} /> : <UsageEllipsisCell text={r.screenRoute} maxWidth={200} color="#888" />}</TableCell>
                <TableCell style={{ whiteSpace: 'nowrap' }}>{r.entityType}{r.entityId ? <b> #{r.entityId}</b> : ''}</TableCell>
                <TableCell><UsageStatusChip code={r.statusCode} /></TableCell>
                <TableCell><UsageDurationBar ms={r.durationMs} max={maxMs} /></TableCell>
                <TableCell style={{ whiteSpace: 'nowrap' }}>{r.ip}{r.details && r.details.forwarded ? <span style={{ color: '#c62828' }}> ⚠</span> : null}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  }

  render() {
    const { intl } = this.props;
    const { filter, mode, autoFlat, loading, loaded, visits, count, page, drawerRow, snack, actionKind, entityType, entityId } = this.state;
    const extra = (
      <Grid item xs={12} md={4}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(e, v) => v && this.setState({ mode: v, autoFlat: false }, () => loaded && this.load(1))}>
            <ToggleButton value="visits">{this.tr('usage.ua.mode.visits', 'Por visita')}</ToggleButton><ToggleButton value="flat">{this.tr('usage.ua.mode.flat', 'Plana')}</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" color="secondary" onClick={() => this.setState({ autoFlat: false }, () => this.load(1))} disabled={loading} startIcon={<PersonSearchIcon />}>{this.tr('usage.ua.search', 'Buscar')}</Button>
          <Tooltip title={this.tr('usage.ua.exportTooltip', 'Exportação assíncrona: o CSV chega por e-mail')}><span><Button variant="outlined" onClick={this.exportCsv} disabled={loading || !filter.userId} startIcon={<FileDownloadIcon />}>{this.tr('usage.ua.export', 'Exportar')}</Button></span></Tooltip>
          {/* Voltar pela história do React Router (recusas/dashboard → linha do tempo → origem). Sem história, vai ao dashboard. Mesmo padrão de entity-access. */}
          <Button variant="outlined" color="primary" startIcon={<ArrowBackIcon />}
            onClick={() => (this.props.history.length > 1 ? this.props.history.goBack() : this.props.history.push(`/${this.props.lang}/usage/dashboard`))}>
            {this.tr('back', 'Voltar')}
          </Button>
        </Box>
      </Grid>
    );
    return (
      <Box>
        <UsagePeriodFilter intl={intl} value={filter} onChange={this.onFilter} showApi={false} showGroup={false} extra={extra} />
        {filter.userId ? <Box style={{ marginTop: 8 }}><Chip size="small" color="primary" label={`${this.tr('usage.ua.userChip', 'Usuário')}: ${filter.userName || `#${filter.userId}`}`} /></Box> : null}
        {mode === 'flat' && (
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12} sm={4} md={2}>
              <TextField select fullWidth size="small" label={this.tr('usage.ua.action', 'Ação')} value={actionKind} InputLabelProps={{ shrink: true }} SelectProps={{ native: true }} onChange={(e) => this.setState({ actionKind: e.target.value })}>
                {KINDS.map((k) => <option key={k} value={k}>{k ? kindLabel(intl, k) : this.tr('usage.ua.action.all', 'Todas')}</option>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={2}><TextField fullWidth size="small" label={this.tr('usage.ua.entityType', 'Entidade (ex.: called)')} value={entityType} InputLabelProps={{ shrink: true }} onChange={(e) => this.setState({ entityType: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4} md={2}><TextField fullWidth size="small" type="number" label={this.tr('usage.ua.entityId', 'Id da entidade')} value={entityId} InputLabelProps={{ shrink: true }} onChange={(e) => this.setState({ entityId: e.target.value })} /></Grid>
          </Grid>
        )}

        <Box style={{ marginTop: 16 }}>
          {loading && <Box display="flex" justifyContent="center" style={{ padding: 16 }}><CircularProgress color="secondary" /></Box>}
          {!loading && loaded && this.renderSummary()}
          {!loading && loaded && autoFlat && <Alert severity="info" style={{ marginBottom: 12 }}>{this.tr('usage.ua.autoFlat', 'Este usuário não tem visitas de tela no período. Os eventos abaixo vieram de chamadas diretas à API (URL colada no navegador, ferramenta ou script), sem passar pelas telas do sistema. Exibindo a lista plana.')}</Alert>}
          {!loading && loaded && mode === 'visits' && (visits.length ? visits.map((v) => <UsageVisitAccordion key={v.screenVisitId} intl={intl} visit={v} loadRows={this.loadVisitRows} onRowClick={this.openRow} />) : <UsageEmpty intl={intl} compact title={this.tr('usage.ua.noVisits', 'Nenhuma tela aberta no período')} />)}
          {!loading && loaded && mode === 'flat' && <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12 }}><CardContent style={{ padding: 0 }}>{this.renderFlat()}</CardContent></Card>}
          {!loading && !loaded && <UsageEmpty intl={intl} icon={<PersonSearchIcon style={{ fontSize: 56 }} />} title={this.tr('usage.ua.empty.title', 'Escolha um usuário e um período')} description={this.tr('usage.ua.empty.description', 'A linha do tempo mostra cada tela aberta e, dentro dela, cada chamada à API, na ordem em que aconteceram.')} />}
          {!loading && loaded && count > PAGE_SIZE && (
            <Box display="flex" alignItems="center" style={{ marginTop: 8, gap: 12 }}>
              <Pagination count={Math.ceil(count / PAGE_SIZE)} page={page} onChange={this.onPage} />
              <Typography variant="caption" style={{ color: '#888' }}>{fmtInt(count)} {mode === 'visits' ? this.tr('usage.ua.visits', 'visita(s)') : this.tr('usage.ua.events', 'evento(s)')}</Typography>
            </Box>
          )}
        </Box>
        <UsageEventDrawer intl={intl} open={!!drawerRow} row={drawerRow} onClose={this.closeDrawer} lang={this.props.lang} history={this.props.history} onCopy={() => this.notify('success', this.tr('usage.copied', 'Copiado!'))} />
        <CustomizedSnackbars variant={snack.variant} message={snack.message} isOpen={snack.open} toClose={this.closeSnack} />
      </Box>
    );
  }
}
